import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Pencil, Trash2, X, Users, UserCheck, AlertCircle } from 'lucide-react';
import { organizations } from '../services/api';

const Organizations = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [notification, setNotification] = useState({ isOpen: false, type: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [formData, setFormData] = useState({ name: '', slug: '', status: 'active' });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const showNotification = (type, message) => {
    setNotification({ isOpen: true, type, message });
    setTimeout(() => setNotification({ isOpen: false, type: '', message: '' }), 4000);
  };

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const response = await organizations.getAll();
      setOrgs(response.data.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user.role === 'super_admin') {
      fetchOrgs();
    }
  }, []);

  // Access check (after all hooks)
  if (user.role !== 'super_admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-700">Acesso negado</h2>
          <p className="text-red-600 mt-1">Apenas super administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: isEditing ? prev.slug : generateSlug(name)
    }));
  };

  const handleOpenCreate = () => {
    setFormData({ name: '', slug: '', status: 'active' });
    setIsEditing(false);
    setSelectedOrg(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (org) => {
    setFormData({ name: org.name, slug: org.slug, status: org.status || 'active' });
    setIsEditing(true);
    setSelectedOrg(org);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedOrg) {
        await organizations.update(selectedOrg.id, formData);
        showNotification('success', 'Organização atualizada com sucesso');
      } else {
        await organizations.create(formData);
        showNotification('success', 'Organização criada com sucesso');
      }
      setIsModalOpen(false);
      fetchOrgs();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao salvar organização';
      showNotification('error', msg);
    }
  };

  const handleDelete = (org) => {
    if (org.id === '00000000-0000-4000-a000-000000000001') {
      showNotification('error', 'Não é possível excluir a organização padrão');
      return;
    }
    if (org.userCount > 0 || org.patientCount > 0) {
      showNotification('error', `Não é possível excluir organização com ${org.userCount} usuários e ${org.patientCount} pacientes`);
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Organização',
      message: `Tem certeza que deseja excluir "${org.name}"? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        try {
          await organizations.delete(org.id);
          showNotification('success', 'Organização excluída com sucesso');
          fetchOrgs();
        } catch (error) {
          const msg = error.response?.data?.message || 'Erro ao excluir organização';
          showNotification('error', msg);
        }
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  // Stats
  const totalOrgs = orgs.length;
  const activeOrgs = orgs.filter(o => o.status !== 'inactive').length;
  const inactiveOrgs = orgs.filter(o => o.status === 'inactive').length;
  const totalUsers = orgs.reduce((sum, o) => sum + (o.userCount || 0), 0);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Notification */}
      {notification.isOpen && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Organizações</h1>
        <button
          onClick={handleOpenCreate}
          className="flex items-center px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nova Organização
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{totalOrgs}</p>
            </div>
            <Building2 className="w-8 h-8 text-brand-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Ativas</p>
              <p className="text-2xl font-bold text-green-600">{activeOrgs}</p>
            </div>
            <UserCheck className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Inativas</p>
              <p className="text-2xl font-bold text-gray-400">{inactiveOrgs}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Usuários (total)</p>
              <p className="text-2xl font-bold text-brand-600">{totalUsers}</p>
            </div>
            <Users className="w-8 h-8 text-brand-500" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : orgs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Nenhuma organização encontrada</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuários</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pacientes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orgs.map(org => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{org.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{org.slug}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      org.status === 'inactive'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {org.status === 'inactive' ? 'Inativa' : 'Ativa'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{org.userCount || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{org.patientCount || 0}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(org.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleOpenEdit(org)}
                      className="text-brand-600 hover:text-brand-800 mr-3"
                      title="Editar"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(org)}
                      className="text-red-600 hover:text-red-800"
                      title="Excluir"
                      disabled={org.id === '00000000-0000-4000-a000-000000000001'}
                    >
                      <Trash2 className={`w-4 h-4 ${org.id === '00000000-0000-4000-a000-000000000001' ? 'opacity-30' : ''}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {isEditing ? 'Editar Organização' : 'Nova Organização'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="Nome da organização"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="slug-da-organizacao"
                />
                <p className="text-xs text-gray-400 mt-1">Identificador único (auto-gerado a partir do nome)</p>
              </div>
              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="active">Ativa</option>
                    <option value="inactive">Inativa</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800"
                >
                  {isEditing ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{confirmDialog.message}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
